import type { FileEntry } from "@codvero/shared";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Download, File, FileImage, Folder, FolderDown, FolderOpen, RefreshCw, Search, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { filesApi } from "@/api/browser.ts";
import { saveToDevice } from "@/features/chat/FilePreview.tsx";
import { cn } from "@/lib/cn.ts";
import { Input, Spinner } from "@/ui/primitives.tsx";

// the row's actions show on hover, and always on a touch screen (no hover there)
const ROW_ACTION = "invisible rounded p-0.5 text-fg-muted hover:text-fg group-hover:visible [@media(hover:none)]:visible";

/** A whole folder as one zip (made on the server; the button waits while it downloads). */
function FolderDownload({ projectId, path }: { projectId: string; path: string }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const name = `${path.split("/").pop() || "folder"}.zip`;
  const label = t("files.downloadFolder", { name: path.split("/").pop() });
  return (
    <button type="button" disabled={busy} className={cn(ROW_ACTION, busy && "visible")} aria-label={label} title={label} onClick={async () => { setBusy(true); try { await saveToDevice(filesApi.zipUrl(projectId, path), name, "application/zip"); } finally { setBusy(false); } }}>
      {busy ? <Spinner className="size-3.5" /> : <FolderDown className="size-3.5" />}
    </button>
  );
}

const fmtSize = (n: number | null) => (n === null ? "" : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`);

/** Lazy workspace tree with filename search, text/image viewer and downloads. */
export function FilesPanel({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const search = useQuery({ queryKey: ["files", projectId, "search", q], queryFn: () => filesApi.search(projectId, q), enabled: q.trim().length >= 2 });
  return (
    <div className="flex h-full flex-col text-sm">
      <div className="flex items-center gap-2 border-b border-border px-2 py-1.5">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute start-2 top-1/2 size-3.5 -translate-y-1/2 text-fg-muted" />
          <Input className="h-8 ps-7 text-xs" placeholder={t("files.search")} value={q} onChange={(e) => setQ(e.target.value)} />
          {q && <button type="button" className="absolute end-2 top-1/2 -translate-y-1/2 text-fg-muted" onClick={() => setQ("")}><X className="size-3.5" /></button>}
        </div>
      </div>
      <div className={cn("grid min-h-0 flex-1", open ? "grid-rows-[minmax(0,2fr)_minmax(0,3fr)]" : "")}>
        <div className="min-h-0 overflow-auto p-1" dir="ltr">
          {q.trim().length >= 2 ? (
            <ul>
              {search.isFetching && <li className="p-2"><Spinner /></li>}
              {search.data?.hits.map((h) => (
                <li key={h.path}>
                  <button type="button" className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-start text-xs hover:bg-surface-2" onClick={() => h.type === "file" && setOpen(h.path)}>
                    {h.type === "dir" ? <Folder className="size-3.5 text-fg-muted" /> : <File className="size-3.5 text-fg-muted" />}<span className="truncate font-mono">{h.path}</span>
                  </button>
                </li>
              ))}
              {search.data && search.data.hits.length === 0 && <li className="p-2 text-xs text-fg-muted">{t("files.noResults")}</li>}
            </ul>
          ) : (
            <DirNode projectId={projectId} path="" depth={0} onOpen={setOpen} openPath={open} />
          )}
        </div>
        {open && <FileViewer projectId={projectId} path={open} onClose={() => setOpen(null)} />}
      </div>
    </div>
  );
}

function DirNode({ projectId, path, depth, onOpen, openPath }: { projectId: string; path: string; depth: number; onOpen: (p: string) => void; openPath: string | null }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const q = useQuery({ queryKey: ["files", projectId, "ls", path], queryFn: () => filesApi.list(projectId, path) });
  if (q.isLoading) return <div className="px-2 py-1" style={{ paddingInlineStart: depth * 12 + 8 }}><Spinner className="size-3.5" /></div>;
  if (!q.data) return null;
  if (q.data.entries.length === 0 && depth === 0) return <p className="p-3 text-xs text-fg-muted">{t("files.empty")}</p>;
  return (
    <ul>
      {q.data.entries.map((e: FileEntry) => {
        const full = path ? `${path}/${e.name}` : e.name;
        const isOpen = expanded.has(e.name);
        return (
          <li key={e.name}>
            <div className={cn("group flex items-center gap-1 rounded px-1 py-0.5 text-xs hover:bg-surface-2", openPath === full && "bg-accent-soft")} style={{ paddingInlineStart: depth * 12 + 4 }}>
              {e.type === "dir" ? (
                <button type="button" className="flex min-w-0 flex-1 items-center gap-1.5" onClick={() => setExpanded((s) => { const n = new Set(s); if (n.has(e.name)) n.delete(e.name); else n.add(e.name); return n; })}>
                  {isOpen ? <ChevronDown className="size-3 shrink-0 text-fg-muted" /> : <ChevronRight className="size-3 shrink-0 text-fg-muted" />}
                  {isOpen ? <FolderOpen className="size-3.5 shrink-0 text-warn" /> : <Folder className="size-3.5 shrink-0 text-warn" />}
                  <span className={cn("truncate", e.hidden && "text-fg-muted")}>{e.name}</span>
                </button>
              ) : (
                <button type="button" className="flex min-w-0 flex-1 items-center gap-1.5" onClick={() => onOpen(full)}>
                  <span className="w-3" />
                  {/\.(png|jpe?g|gif|webp|svg)$/i.test(e.name) ? <FileImage className="size-3.5 shrink-0 text-fg-muted" /> : <File className="size-3.5 shrink-0 text-fg-muted" />}
                  <span className={cn("truncate", e.hidden && "text-fg-muted")}>{e.name}</span>
                  <span className="ms-auto shrink-0 text-[10px] text-fg-muted">{fmtSize(e.size)}</span>
                </button>
              )}
              {e.type === "file" && <a href={filesApi.rawUrl(projectId, full, true)} download className={ROW_ACTION} aria-label={t("files.download")}><Download className="size-3.5" /></a>}
              {e.type === "dir" && <FolderDownload projectId={projectId} path={full} />}
            </div>
            {e.type === "dir" && isOpen && <DirNode projectId={projectId} path={full} depth={depth + 1} onOpen={onOpen} openPath={openPath} />}
          </li>
        );
      })}
    </ul>
  );
}

function FileViewer({ projectId, path, onClose }: { projectId: string; path: string; onClose: () => void }) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ["files", projectId, "content", path], queryFn: () => filesApi.content(projectId, path) });
  const f = q.data;
  const isImage = f?.mime.startsWith("image/");
  return (
    <div className="flex min-h-0 flex-col border-t border-border">
      <div className="flex items-center gap-2 border-b border-border px-2 py-1 text-xs">
        <span dir="ltr" className="min-w-0 flex-1 truncate font-mono">{path}</span>
        {f && <span className="text-fg-muted">{fmtSize(f.size)}</span>}
        <a href={filesApi.rawUrl(projectId, path, true)} download className="rounded p-1 text-fg-muted hover:text-fg" aria-label={t("files.download")}><Download className="size-3.5" /></a>
        <button type="button" className="rounded p-1 text-fg-muted hover:text-fg" aria-label={t("common.close")} onClick={onClose}><X className="size-3.5" /></button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto bg-bg">
        {q.isLoading && <div className="p-3"><Spinner /></div>}
        {f && isImage && <img src={filesApi.rawUrl(projectId, path)} alt={path} className="max-w-full p-2" />}
        {f && !isImage && f.binary && <p className="p-3 text-xs text-fg-muted">{t("files.binary")}</p>}
        {f && !isImage && !f.binary && (
          <pre dir="ltr" className="whitespace-pre-wrap break-words p-3 font-mono text-[11px] leading-5">{f.content}{f.truncated && `\n…[${t("files.truncated")}]`}</pre>
        )}
      </div>
    </div>
  );
}

export const FilesRefresh = RefreshCw;
